import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import {
    Box, Button, Center, Flex, Image, Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay,
    Spinner, Text, useToast,
} from '@chakra-ui/react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import useSocketReconnect from '../hooks/useSocketReconnect';
import { useChatEnabledStatus } from '../mobile/hooks/useChatEnabled';
import useChatSocket from '../mobile/hooks/useChatSocket';
import useOptimisticSend from '../mobile/hooks/useOptimisticSend';
import useStickToBottom from '../hooks/useStickToBottom';
import MessageList from '../mobile/chat/MessageList';
import Composer from '../mobile/chat/Composer';
import MessageContextMenu from '../mobile/chat/MessageContextMenu';
import LogoPulseLoader from '../components/LogoPulseLoader';

const PAGE_SIZE = 30;
const POLL_MS = 30000;

// [B3] 家長對話室（/parent/chat/:id — 逐字對齊 B2 推播 deep-link 約定，別改路徑）。
// 復用老師/家長共用元件 MessageList／Composer／CommBookCard（src/mobile/chat/*）。
//
// [B3 補強] 原任務簡述誤判「家長沒有 WebSocket」，經 Fable 逐條核對 websockets.py
// 後修正（fast-handshake 不查角色、fallback allowlist 含 parent、broadcast_chat_message
// 本來就會對 parent_ids send_personal_message）——即時性現在是 WS 為主，以下當 fallback
// （WS 斷線／未連線時的雙保險，不因為接了 WS 就拿掉）：
//   ①FCM 推播（B2 deep-link 已接） ②視窗回前景 refetch（visibilitychange）
//   ③開著房間時 30 秒溫和輪詢 ④送出後本地即時 append
// 輪詢間隔維持 30 秒不因 WS 連線而放寬到 60 秒：省下的流量微乎其微（單一開著的房間，
// 一次 GET 差在 30 或 60 秒打一次），但要多帶一個「依連線狀態動態調整 interval」的
// effect 依賴/重建邏輯，多的正確性風險不划算，故不做。
export default function ParentChatRoomPage() {
    const { id } = useParams();
    const convId = Number(id);
    const location = useLocation();
    const { user } = useAuth();
    const { enabled, loading: gateLoading } = useChatEnabledStatus();
    const toast = useToast();

    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [roomInfo, setRoomInfo] = useState(
        location.state?.studentName
            ? { studentId: location.state.studentId, studentName: location.state.studentName, className: location.state.className }
            : null
    );
    const [lightbox, setLightbox] = useState(null);
    const [contextMenuTarget, setContextMenuTarget] = useState(null);
    const scrollerRef = useRef(null);
    const firstLoadRef = useRef(true);
    // 鍵盤升降時把畫面釘在最新訊息（除非家長正在往上翻舊訊息）
    useStickToBottom(scrollerRef);
    // 「載入更早訊息」是往上 prepend，不應把畫面拉回最底部（下方捲動 effect 依此跳過一次）。
    // 比照老師端 feat/b1-chat-ui commit b2a005b 的修法，家長對話室內建同樣的防護。
    const skipScrollRef = useRef(false);

    const appendMessage = useCallback((incoming) => {
        setMessages((prev) => (prev.some((m) => m.id === incoming.id) ? prev : [...prev, incoming]));
    }, []);

    // 訊息被收回：就地換掉同 id 的那一則（後端回的版本 text/image_url 已清空、is_recalled=true）。
    // 不能走 appendMessage——那支是「沒見過才加進去」，同一個 id 會被去重掉，畫面上仍是舊內容。
    // 家長端刻意只「接收」收回、不提供收回入口（後端允許發送者本人收回，UI 這一版先只給老師）。
    const replaceMessage = useCallback((updated) => {
        if (!updated?.id) return;
        setMessages((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
    }, []);

    // 送出＝樂觀更新（比照 LINE）：訊息立刻出現在畫面（傳送中半透明），成功後由 hook 換成
    // 後端回傳的真訊息並走既有 appendMessage 去重（處理「WS 先到／POST 回應後到」的競態），
    // 失敗則氣泡旁顯示紅色驚嘆號、點一下重送。
    // 宣告位置刻意放在這裡（appendMessage 之後、下方捲動 effect 之前）：捲動 effect 需要讀
    // pending.length 才能在樂觀訊息插入時一起捲到底，放在檔案後段會造成 TDZ。
    const { pending, send: handleSend, retry: handleRetry } = useOptimisticSend({
        convId,
        user,
        appendMessage,
        // 真訊息一到就丟掉對應的樂觀副本。WS 是獨立管道，可能先於 POST 回應抵達，
        // 不對帳的話兩者會同時顯示＝使用者看到重複訊息（user 2026-08-13 回報）。
        messages,
    });

    // 離開房間時通知列表刷新未讀數（比照 ParentCommBookPage 既有 unmount 慣例）。
    // 對話列表在「對話室從列表點進」的情境下是持續掛載的底層（見 ParentLayout chatSection），
    // 不會因為房間疊上/收起而重新掛載，所以需要這個事件才會補一次 fetch 拿到最新已讀狀態。
    useEffect(() => {
        return () => {
            window.dispatchEvent(new Event('data-refresh'));
        };
    }, []);

    // 初始載入 + 進頁即標記已讀。setLoading(true) 特意包一層 Promise.resolve().then() 才呼叫——
    // convId 在極少數情況下可能於掛載後變化（例如瀏覽器上一頁/下一頁在兩個房間間跳），這時
    // 需要真的把 loading 重新打開；但直接在效果本體同步呼叫 setState 會被 eslint
    // react-hooks/set-state-in-effect 擋下來，所以延到下一個 microtask 才設（使用者感覺不出差異，
    // 網路請求本身就比一個 microtask tick 慢很多）。
    useEffect(() => {
        if (!enabled) return;
        let alive = true;
        firstLoadRef.current = true;
        Promise.resolve()
            .then(() => { if (alive) setLoading(true); })
            .then(() => authAPI.chat.messages(convId, { limit: PAGE_SIZE }))
            .then((r) => {
                if (!alive) return;
                const data = r.data || [];
                setMessages(data);
                setHasMore(data.length === PAGE_SIZE);
            })
            .catch(() => { if (alive) setMessages([]); })
            .finally(() => { if (alive) setLoading(false); });
        authAPI.chat.read(convId).catch(() => {});
        return () => { alive = false; };
    }, [convId, enabled]);

    // 沒有從列表帶入房間資訊時（例如深連結冷啟動、或重新整理）→ 退而用 conversations 找同一筆，
    // 補學生/課程名稱＋student_id（簽收要用）。
    useEffect(() => {
        if (roomInfo || !enabled) return;
        let alive = true;
        authAPI.chat.conversations()
            .then((r) => {
                if (!alive) return;
                const found = (r.data || []).find((c) => c.id === convId);
                if (found) setRoomInfo({ studentId: found.student_id, studentName: found.student_name, className: found.class_name });
            })
            .catch(() => {});
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [convId, enabled]);

    useEffect(() => {
        if (firstLoadRef.current && messages.length > 0) {
            firstLoadRef.current = false;
        }
        if (skipScrollRef.current) {
            skipScrollRef.current = false;
            return;
        }
        // 直接設訊息區自己的 scrollTop，不用 scrollIntoView：後者會沿著祖先鏈一路捲，
        // 在 iOS 上可能連外層一起捲走（畫面跳動的來源之一）。
        const el = scrollerRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
            // 再補一個影格：此刻 React 才剛把新訊息寫進 DOM，圖片與換行都還沒完成版面計算，
            // scrollHeight 會偏小。後續的圖片載入由 useStickToBottom 接手。
            requestAnimationFrame(() => {
                const e2 = scrollerRef.current;
                if (e2) e2.scrollTop = e2.scrollHeight;
            });
        }
        // pending.length 一併納入：樂觀訊息插入時（以及成功後被移除、換成真訊息時）都要捲到底，
        // 否則自己剛送出的那則會停在畫面外，正好抵銷掉樂觀更新想帶來的即時感。
    }, [messages.length, pending.length]);

    // 抓最新一頁、與目前已載入的訊息合併（只新增未見過的，不動已載入的較舊訊息／捲動位置）。
    // 用於 30 秒輪詢 + 視窗回前景，兩者共用同一份邏輯。
    const fetchLatest = useCallback(() => {
        return authAPI.chat.messages(convId, { limit: PAGE_SIZE }).then((r) => {
            const data = r.data || [];
            setMessages((prev) => {
                const known = new Set(prev.map((m) => m.id));
                const fresh = data.filter((m) => !known.has(m.id));
                // 已載入的訊息中「後端說已收回、本地還留著舊內容」的那幾則要換掉。
                // 為什麼這裡也要處理：WS 是家長端的主要即時管道，但它會斷（App 切到背景、
                // 網路切換），輪詢/回前景是唯一的 fallback。少了這段，老師收回的內容會一直
                // 留在家長畫面上直到重開頁面——收回等於沒收回。
                // 刻意只換「翻成已收回」的那幾則，不整批覆蓋已載入的訊息：本檔的簽收是樂觀
                // 更新，靠的正是「後端資料只會補新訊息、不會覆寫既有物件」這個前提
                //（見 handleSign 的說明）。而聯絡簿訊息後端不允許收回，所以這條路徑永遠
                // 碰不到帶 signature 的那些訊息，兩邊不會打架。
                const recalled = new Map(
                    data.filter((m) => m.is_recalled === true && known.has(m.id)).map((m) => [m.id, m])
                );
                const needPatch = prev.some((m) => m.is_recalled !== true && recalled.has(m.id));
                if (!fresh.length && !needPatch) return prev;
                const base = needPatch
                    ? prev.map((m) => (m.is_recalled !== true && recalled.has(m.id) ? recalled.get(m.id) : m))
                    : prev;
                if (!fresh.length) return base;
                return [...base, ...fresh].sort((a, b) => a.id - b.id);
            });
        }).catch(() => {});
    }, [convId]);

    // 開著房間時 30 秒溫和輪詢 + 視窗回前景 refetch（比照 NotificationContext.jsx 既有
    // visibilitychange pattern）；回前景視為「正在看」，順手再回報一次已讀。
    useEffect(() => {
        if (!enabled) return;
        const interval = setInterval(fetchLatest, POLL_MS);
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                fetchLatest();
                authAPI.chat.read(convId).catch(() => {});
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [convId, enabled, fetchLatest]);

    // WS 每次重新連上就補抓一次。WS 沒有補送機制，斷線那幾秒送達的訊息永久遺失
    //（見 src/hooks/useSocketReconnect.js）。30 秒輪詢雖然也會補上，但最壞情況要等滿 30 秒，
    // 家長在對話中盯著螢幕的那半分鐘就是「對方明明送了我卻沒看到」。
    // 沿用既有的 fetchLatest（不另造一套）：它本來就是「只補沒見過的、不動已載入訊息與捲動
    // 位置」，而且沒有新訊息時直接原樣返回 prev＝不觸發 re-render，畫面不會被拉走。
    // 順手補一次已讀，比照上面 visibilitychange 的處理；但要先確認畫面真的在前景——重連常
    // 發生在 App 還在背景的時候，那時人根本沒在看，標成已讀會讓未讀提示無聲消失。
    useSocketReconnect(() => {
        fetchLatest();
        if (document.visibilityState === 'visible') {
            authAPI.chat.read(convId).catch(() => {});
        }
    });

    // [B3 補強] WS 即時：只處理當前這條對話；補進來的訊息若不是自己發的，順手再標記一次
    // 已讀（人正在看）。比照老師端 feat/b1-chat-ui 的 MobileChatRoom 既有寫法逐字對齊，用
    // appendMessage 既有的 id 去重（同一則訊息若 WS 和輪詢/送出後 append 前後腳到，不會重複）。
    // 第二個 handler 收「訊息被收回」：老師收回時，正開著房間的家長畫面要立刻變成墓碑。
    // 刻意不補打已讀——收回不是新訊息，多打一次只是無謂請求。
    useChatSocket((payload) => {
        if (payload.conversation_id !== convId) return;
        appendMessage(payload.message);
        if (payload.message?.sender_id !== user?.id) {
            authAPI.chat.read(convId).catch(() => {});
        }
    }, (payload) => {
        if (payload.conversation_id !== convId) return;
        replaceMessage(payload.message);
    });

    const loadMore = async () => {
        if (!messages.length || loadingMore) return;
        setLoadingMore(true);
        try {
            const beforeId = messages[0].id;
            const r = await authAPI.chat.messages(convId, { before_id: beforeId, limit: PAGE_SIZE });
            const older = r.data || [];
            setHasMore(older.length === PAGE_SIZE);
            const fresh = older.filter((m) => !messages.some((p) => p.id === m.id));
            if (fresh.length) {
                skipScrollRef.current = true;
                setMessages((prev) => [...fresh.filter((m) => !prev.some((p) => p.id === m.id)), ...prev]);
            }
        } catch {
            // 靜默失敗，保留目前已載入的訊息
        } finally {
            setLoadingMore(false);
        }
    };

    // 簽收：復用既有 POST /communication-books/sign（ParentCommBookPage 現行呼叫的同一支）。
    // 就地把本地已載入訊息中同一本聯絡簿的 signature 標記為已簽名，不整串重載（避免打斷捲動
    // 位置）；理論上一本聯絡簿在同一串只會出現一次，用 map 全比對防萬一。
    //
    // 改樂觀更新（比照 useOptimisticSend 送訊息）：原本是 await 完 API 才 setMessages，家長按下
    // 去得盯著轉圈等一次完整來回（實機回報約 5 秒）。簽收是「一定會成功」的動作，為了罕見的
    // 失敗情境讓每一次成功都付出等待，划不來；改成先寫本地、失敗再回滾。
    //
    // 為什麼敢先寫（不會被後端資料打架）：本檔所有「後端訊息進來」的路徑都是先以 id 去重、
    // 只補沒見過的那幾則，不會覆寫已載入訊息的物件——fetchLatest 只取 known 以外的 fresh、
    // appendMessage 撞到既有 id 直接原樣返回、loadMore 只往前 prepend。所以樂觀寫進去的
    // signature 不會被 30 秒輪詢或 WS 打回未簽名。唯一整批覆蓋的是初始載入的 setMessages(data)，
    // 那是重新掛載後拿後端真實狀態，本來就該以後端為準。
    const handleSign = useCallback(async (bookId) => {
        if (!roomInfo?.studentId) {
            // 深連結冷啟動且 conversations fallback 也失敗時 roomInfo 會是 null，簽收帶不出
            // student_id。原本靜默 return，家長按了完全沒反應、看起來像當掉，至少讓他知道怎麼救。
            toast({ title: '目前無法簽名，請重新進入對話後再試', status: 'warning', duration: 2000 });
            return;
        }

        // 回滾要能還原成「原本的樣子」，所以用 updater 就地改 signature、保留後端可能新增的其他欄位
        const patchSignature = (updater) => setMessages((prev) => prev.map((m) => (
            m.content_type === 'comm_book' && m.comm_book?.id === bookId
                ? { ...m, comm_book: { ...m.comm_book, signature: updater(m.comm_book.signature) } }
                : m
        )));

        const signedAt = new Date().toISOString();
        patchSignature((s) => ({ ...s, signed: true, signed_at: signedAt }));

        try {
            await authAPI.communicationBook.signBook(bookId, {
                book_id: bookId,
                student_id: roomInfo.studentId,
                comment: '',
            });
        } catch {
            // 失敗回滾成未簽名，簽收鈕會原樣回來。刻意不做重試 UI（送訊息才需要，因為那則訊息
            // 已經佔著版面）：簽收回滾後畫面就是「沒簽」，家長再按一次就是重試，不必多一層互動。
            patchSignature((s) => ({ ...s, signed: false, signed_at: null }));
            toast({ title: '簽名失敗，請再試一次', status: 'error', duration: 2000 });
        }
    }, [roomInfo?.studentId, toast]);

    // 防呆：直接網址列開 /parent/chat/:id 但旗標還沒查完 → 先 loading，避免誤導回聯絡簿舊頁
    // （深連結冷啟動是這裡的主要入口，見上方檔案說明；旗標查完才決定要不要導頁）。
    if (gateLoading) {
        return <Center h="60vh"><LogoPulseLoader /></Center>;
    }
    if (!enabled) {
        return <Navigate to="/parent/communication-book" replace />;
    }

    return (
        <Flex direction="column" h="100%">
            <Box ref={scrollerRef} flex="1" minH={0} overflowY="auto" overscrollBehavior="contain" px={3} py={2}>
                {(roomInfo?.studentName || roomInfo?.className) && (
                    <Text fontSize="xs" color="onCanvas.300" textAlign="center" mb={2}>
                        {roomInfo.studentName}
                        {roomInfo.className ? `．${roomInfo.className}` : ''}
                    </Text>
                )}

                {loading ? (
                    <Center py={16}><Spinner /></Center>
                ) : (
                    <>
                        {hasMore && (
                            <Flex justify="center" mb={2}>
                                <Button size="xs" variant="ghost" color="onCanvas.400" onClick={loadMore} isLoading={loadingMore}>
                                    載入更早訊息
                                </Button>
                            </Flex>
                        )}
                        <MessageList
                            messages={messages}
                            currentUserId={user?.id}
                            onImageClick={setLightbox}
                            onSign={handleSign}
                            pending={pending}
                            onRetry={handleRetry}
                            onContextMenu={setContextMenuTarget}
                        />
                    </>
                )}
            </Box>

            {/* [Stage3 整合] 家長傳圖改走 E1 的 chat 專用上傳端點（旗標＋對話參與者雙門），
                403 問題已解，allowImage 恢復預設開啟。 */}
            <Composer
                onSend={handleSend}
                disabled={loading}
                uploadFn={(fd) => authAPI.chat.upload(convId, fd)}
            />

            {/* 功能選單：家長長按訊息後出現（複製），家長端不提供收回入口 */}
            <MessageContextMenu
                target={contextMenuTarget}
                onClose={() => setContextMenuTarget(null)}
            />

            {/* 照片放大檢視（react-zoom-pan-pinch，比照 ParentCommBookPage 既有 lightbox 樣式） */}
            <Modal isOpen={!!lightbox} onClose={() => setLightbox(null)} size="full" isCentered>
                <ModalOverlay bg="blackAlpha.900" />
                <ModalContent bg="transparent" boxShadow="none" m={0}>
                    <ModalCloseButton
                        color="white" bg="blackAlpha.600" _hover={{ bg: 'blackAlpha.800' }}
                        borderRadius="full" zIndex={200} size="lg"
                        position="fixed" top="calc(env(safe-area-inset-top) + 12px)" right="16px"
                    />
                    <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
                        {lightbox && (
                            <TransformWrapper doubleClick={{ mode: 'reset' }}>
                                <TransformComponent
                                    wrapperStyle={{ width: '100vw', height: '100vh' }}
                                    contentStyle={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <Image src={lightbox} w="100vw" maxH="100vh" objectFit="contain" />
                                </TransformComponent>
                            </TransformWrapper>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Flex>
    );
}
